import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import axiosInstance from '../constants/axiosInstance';
import i18n from '../i18n';

import { httpErrorFrom } from './errors';
import type { ErrorFactory } from './errors';
import { endLocalSession, isSessionLoss } from './session';

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

export interface RequestOpts {
  readonly params?: Record<string, unknown>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly timeoutMs?: number;
}

export interface ApiClient {
  request(method: HttpMethod, url: string, opts?: RequestOpts): Promise<unknown>;
}

export const unwrapEnvelope = (body: unknown): unknown =>
  body && typeof body === 'object' && 'data' in body ? (body as { data: unknown }).data : body;

const statusOf = (rejection: unknown): number => {
  const status = (rejection as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' ? status : 0;
};

const withCause = (error: Error, cause: unknown): Error => {
  if (error.cause === undefined) error.cause = cause;
  return error;
};

export const REQUEST_TIMEOUT_MS = 30_000;

export interface ApiClientOptions {
  readonly toError?: ErrorFactory;
  readonly onSessionLost?: () => void;
  readonly timeoutMs?: number;
  readonly baseURL?: string;
}

const configuredUrl = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed === '' ? undefined : trimmed;
};

export const createApiClient = (opts: ApiClientOptions = {}): ApiClient => {
  const toError = opts.toError ?? httpErrorFrom;
  const onSessionLost = opts.onSessionLost ?? endLocalSession;
  const timeout = opts.timeoutMs ?? REQUEST_TIMEOUT_MS;
  const baseURL = configuredUrl(opts.baseURL);

  const request = async (method: HttpMethod, url: string, options: RequestOpts = {}): Promise<unknown> => {
    const controller = new AbortController();
    const forwardAbort = () => controller.abort();
    options.signal?.addEventListener('abort', forwardAbort, { once: true });
    if (options.signal?.aborted) controller.abort();
    const budget = options.timeoutMs ?? timeout;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, budget);

    const config: AxiosRequestConfig = {
      method,
      baseURL,
      url,
      params: options.params,
      data: options.body,
      signal: controller.signal,
      validateStatus: () => true,
    };

    let res: AxiosResponse;
    try {
      res = await axiosInstance().request(config);
    } catch (rejection) {
      if (timedOut) {
        throw withCause(
          toError(0, { message: i18n.t('common.requestTimedOut', 'The server took too long to answer. Try again.') }),
          new DOMException(`No response within ${budget} ms`, 'TimeoutError'),
        );
      }
      if (options.signal?.aborted) throw rejection;
      throw withCause(toError(statusOf(rejection), rejection), rejection);
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', forwardAbort);
    }

    if (isSessionLoss(res.status, res.data)) onSessionLost();
    if (res.status < 200 || res.status >= 300) throw toError(res.status, res.data);
    return unwrapEnvelope(res.data);
  };

  return { request };
};
